import { Component, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { UploadStateService } from './upload-state.service';

interface UploadedFile {
  name: string;
  size: number;
  file: File;
  status: 'pending' | 'success' | 'error';
  csvHeaders?: string[];
  columnMapping?: ColumnMapping;
  needsMapping?: boolean;
}

interface ColumnMapping {
  MD: string;
  INC: string;
  AZI: string;
}

interface UploadResult {
  filename: string;
  api?: string;
  flag?: boolean;
  distance?: number;
  tolerance?: number;
  error?: string;
}

interface UploadResponse {
  results: UploadResult[];
}

@Component({
  selector: 'app-upload',
  standalone: false,
  templateUrl: './upload.html',
  styleUrl: './upload.scss',
})
export class Upload {
  isDragging = false;
  requiredColumns = ['MD', 'INC', 'AZI'];
  currentMappingFile: UploadedFile | null = null;
  showMappingDialog = false;
  showResultsDialog = false;
  isUploading = false;
  apiBaseUrl = environment.apiUrl;
  epsgCodes = [
    { code: 32065, name: 'NAD27 / Louisiana South (EPSG:32065)' }
  ];

  // Matches API number patterns like 42-123-45678 or 4212345678 (with or without dashes)
  private readonly apiNumberPattern = /\d{2}-?\d{3}-?\d{5}/;

  constructor(
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
    public state: UploadStateService
  ) {}

  onFileSelected(event: any): void {
    const files: FileList = event.target.files;
    this.handleFiles(files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  private async handleFiles(files: FileList): Promise<void> {
    const rejectedFiles: string[] = [];

    for (const file of Array.from(files)) {
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        if (!this.apiNumberPattern.test(file.name)) {
          rejectedFiles.push(file.name);
          continue;
        }

        const headers = await this.readCSVHeaders(file);
        const missingColumns = this.checkRequiredColumns(headers);
        
        const uploadedFile: UploadedFile = {
          name: file.name,
          size: file.size,
          file: file,
          status: missingColumns.length > 0 ? 'pending' : 'success',
          csvHeaders: headers,
          needsMapping: missingColumns.length > 0,
          columnMapping: this.createDefaultMapping(headers)
        };
        
        this.state.uploadedFiles.push(uploadedFile);
        
        // If columns are missing, open mapping dialog for this file
        if (missingColumns.length > 0) {
          this.openMappingDialog(uploadedFile);
        }
      } else {
        alert(`${file.name} is not a CSV file`);
      }
    }

    if (rejectedFiles.length > 0) {
      alert(
        `The following file(s) were rejected because they do not contain an API number in the filename:\n\n` +
        rejectedFiles.join('\n')
      );
    }

    // Trigger change detection after all files are processed
    this.cdr.detectChanges();
  }

  private readCSVHeaders(file: File): Promise<string[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        const firstLine = text.split('\n')[0];
        const headers = firstLine.split(',').map(h => h.trim().replace(/"/g, ''));
        resolve(headers);
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }

  private checkRequiredColumns(headers: string[]): string[] {
    const headersUpper = headers.map(h => h.toUpperCase());
    return this.requiredColumns.filter(
      col => !headersUpper.includes(col.toUpperCase())
    );
  }

  private createDefaultMapping(headers: string[]): ColumnMapping {
    const mapping: any = {};
    const headersUpper = headers.map(h => h.toUpperCase());
    
    this.requiredColumns.forEach(reqCol => {
      const index = headersUpper.indexOf(reqCol.toUpperCase());
      mapping[reqCol] = index !== -1 ? headers[index] : '';
    });
    
    return mapping as ColumnMapping;
  }

  removeFile(index: number): void {
    this.state.uploadedFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  openMappingDialog(file: UploadedFile): void {
    this.currentMappingFile = file;
    this.showMappingDialog = true;
  }

  closeMappingDialog(): void {
    this.showMappingDialog = false;
    this.currentMappingFile = null;
  }

  getColumnMapping(column: string): string {
    if (!this.currentMappingFile?.columnMapping) return '';
    return this.currentMappingFile.columnMapping[column as keyof ColumnMapping] || '';
  }

  setColumnMapping(column: string, value: string): void {
    if (!this.currentMappingFile?.columnMapping) return;
    (this.currentMappingFile.columnMapping as any)[column] = value;
  }

  isHeaderUsed(header: string, currentCol: string): boolean {
    if (!this.currentMappingFile?.columnMapping) return false;
    const mapping = this.currentMappingFile.columnMapping;
    return this.requiredColumns.some(
      col => col !== currentCol && mapping[col as keyof ColumnMapping] === header
    );
  }

  saveMappingAndClose(): void {
    if (this.currentMappingFile && this.currentMappingFile.columnMapping) {
      const mapping = this.currentMappingFile.columnMapping;
      
      // Validate that all required columns are mapped
      const unmappedColumns: string[] = [];
      
      if (!mapping.MD || mapping.MD.trim() === '') unmappedColumns.push('MD');
      if (!mapping.INC || mapping.INC.trim() === '') unmappedColumns.push('INC');
      if (!mapping.AZI || mapping.AZI.trim() === '') unmappedColumns.push('AZI');
      
      if (unmappedColumns.length > 0) {
        alert(`Please map all required columns: ${unmappedColumns.join(', ')}`);
        return;
      }
      
      this.currentMappingFile.needsMapping = false;
      this.currentMappingFile.status = 'success';
    }
    
    this.closeMappingDialog();
  }

  uploadFiles(): void {
    if (this.state.uploadedFiles.length === 0) {
      alert('Please select files to upload');
      return;
    }

    if (this.state.toleranceThreshold === null || this.state.toleranceThreshold < 0 || this.state.toleranceThreshold > 10000) {
      alert('Please enter a valid tolerance threshold (must be between 0 and 10000)');
      return;
    }
    
    // Check if any files still need mapping
    const unmappedFiles = this.state.uploadedFiles.filter(f => f.needsMapping);
    if (unmappedFiles.length > 0) {
      alert(`Please map columns for: ${unmappedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    // Create FormData for multipart/form-data upload
    const formData = new FormData();
    
    // Add tolerance threshold
    formData.append('tolerance_threshold', this.state.toleranceThreshold.toString());
    
    // Add EPSG code
    formData.append('epsg_code', this.state.selectedEpsgCode.toString());
    
    // Add each CSV file with its column mapping
    this.state.uploadedFiles.forEach((uploadedFile, index) => {
      // Add the actual file
      formData.append('files', uploadedFile.file, uploadedFile.file.name);
      
      // Add column mapping as JSON string
      formData.append(`mapping_${index}`, JSON.stringify({
        filename: uploadedFile.file.name,
        columns: uploadedFile.columnMapping
      }));
    });
    
    this.isUploading = true;
    this.state.uploadError = null;
    this.state.uploadResults = [];

    this.http.post<UploadResponse>(`${this.apiBaseUrl}/upload`, formData).subscribe({
      next: (response) => {
        this.state.uploadResults = response.results || [];
        this.isUploading = false;
        this.showResultsDialog = true;
        this.cdr.detectChanges();
      },
      error: (err) => {
        const message = err?.error?.detail || err?.message || 'Upload failed';
        this.state.uploadError = message;
        this.isUploading = false;
        this.cdr.detectChanges();
      }
    });
  }

  clearAll(): void {
    this.state.clearAll();
  }

  openResultsDialog(): void {
    this.showResultsDialog = true;
  }

  closeResultsDialog(): void {
    this.showResultsDialog = false;
  }
}
